import ContentCopy from '@mui/icons-material/ContentCopy';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import { Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/system';
import { t } from 'i18next';
import { useState } from 'react';

import { CopyToClipboard } from '~components/CopyToClipboard/CopyToClipboard';

import ShareResourceBannerExamples from './ShareResourceBannerExamples';
import ShareResourceBannerGuides from './ShareResourceBannerGuides';
import ShareResourceBannerSteps from './ShareResourceBannerSteps';
import { ShareResourceBannerConfig } from './shareResourceBannerTypes';
import { toDisplayUrl } from './shareResourceBannerUtils';

interface ShareResourceBannerProps {
  config: ShareResourceBannerConfig;
  sx?: SxProps<Theme>;
}

const sectionLabelSx = (theme: Theme) =>
  ({
    color: theme.brandV2.colors.treadGray3,
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.06em',
    mb: 1,
    textTransform: 'uppercase',
  }) as const;

const ShareResourceBanner = ({ config, sx }: ShareResourceBannerProps) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    testIdPrefix,
    Icon,
    primaryLink,
    translationNamespace,
    steps,
    stepsLayout = 'row',
    showStepsSectionLabel = true,
    aside,
  } = config;

  const hasPrimaryLink = primaryLink !== undefined;
  const asideSectionLabelKey = `${translationNamespace}.${aside.kind}.section_label`;
  const toggleLabel = isExpanded
    ? t(`${translationNamespace}.collapse`)
    : t(`${translationNamespace}.expand`);
  const chevronSx = {
    color: theme.brandV2.colors.treadGray2,
    transform: isExpanded ? 'rotate(180deg)' : 'none',
    transition: 'transform 0.18s ease',
  } as const;
  const bannerBodyId = `${testIdPrefix}-banner-body`;

  return (
    <Box
      data-test-id={`${testIdPrefix}-banner`}
      sx={[
        {
          backgroundColor: theme.semantic.surfaceRaised,
          border: `1px solid ${theme.brandV2.colors.treadGray7}`,
          borderRadius: 3,
          maxWidth: `calc(100% - ${theme.spacing(3)})`,
          overflow: 'hidden',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {/* The whole header row toggles, so the chevron is not the only target;
          the controls inside it stop the click from reaching this handler. */}
      <Box
        onClick={() => setIsExpanded((previouslyExpanded) => !previouslyExpanded)}
        sx={{
          alignItems: 'center',
          cursor: 'pointer',
          display: 'flex',
          gap: 1.5,
          p: 1.5,
        }}
      >
        <Box
          aria-hidden
          sx={{
            alignItems: 'center',
            backgroundColor: theme.palette.primary.main,
            borderRadius: 2,
            color: theme.palette.primary.contrastText,
            display: 'flex',
            flexShrink: 0,
            height: 34,
            justifyContent: 'center',
            width: 34,
          }}
        >
          <Icon sx={{ fontSize: 18 }} />
        </Box>

        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexGrow: 1,
            flexWrap: 'wrap',
            gap: 1.25,
            minWidth: 0,
          }}
        >
          <Typography
            variant="body1"
            sx={{ color: theme.brandV2.colors.treadGray1, fontWeight: 600 }}
          >
            {t(`${translationNamespace}.title`)}
          </Typography>
          {hasPrimaryLink ? (
            <Typography
              component="code"
              sx={{
                backgroundColor: theme.brandV2.colors.treadGray8,
                border: `1px solid ${theme.brandV2.colors.treadGray7}`,
                borderRadius: 1.5,
                color: theme.brandV2.colors.treadGray2,
                fontFamily: 'monospace',
                fontSize: 13,
                px: 1,
                py: 0.25,
              }}
            >
              {/* The chip drops the scheme for a compact display, while copy
                  always writes the full URL to the clipboard. */}
              {toDisplayUrl(primaryLink)}
            </Typography>
          ) : null}
        </Box>

        <Box sx={{ alignItems: 'center', display: 'flex', flexShrink: 0, gap: 0.5 }}>
          {hasPrimaryLink ? (
            <Box
              data-test-id={`${testIdPrefix}-copy-button`}
              onClick={(event) => event.stopPropagation()}
            >
              <CopyToClipboard
                hideTextWhileCopying
                textToCopy={primaryLink}
                sx={{
                  border: `1px solid ${theme.brandV2.colors.treadGray7}`,
                  borderRadius: 1.5,
                  color: theme.brandV2.colors.treadGray2,
                  gap: 0.5,
                  px: 1,
                  py: 0.75,
                }}
              >
                <ContentCopy sx={{ fontSize: 16 }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t(`${translationNamespace}.copy_link`)}
                </Typography>
              </CopyToClipboard>
            </Box>
          ) : null}

          <IconButton
            aria-controls={bannerBodyId}
            aria-expanded={isExpanded}
            aria-label={toggleLabel}
            data-test-id={`${testIdPrefix}-expand-toggle`}
            onClick={(event) => {
              event.stopPropagation();
              setIsExpanded((previouslyExpanded) => !previouslyExpanded);
            }}
            size="small"
          >
            <ExpandMore sx={chevronSx} />
          </IconButton>
        </Box>
      </Box>

      <Collapse in={isExpanded} unmountOnExit>
        <Box
          id={bannerBodyId}
          sx={{
            display: 'grid',
            gap: 2.5,
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.4fr) minmax(0, 1fr)' },
            pb: 2,
            px: 1.5,
          }}
        >
          <Box>
            {showStepsSectionLabel ? (
              <Typography variant="body2" sx={sectionLabelSx(theme)}>
                {t(`${translationNamespace}.how_it_works`)}
              </Typography>
            ) : null}
            <ShareResourceBannerSteps layout={stepsLayout} steps={steps} />
          </Box>
          <Box>
            <Typography variant="body2" sx={sectionLabelSx(theme)}>
              {t(asideSectionLabelKey)}
            </Typography>
            {aside.kind === 'examples' ? (
              <ShareResourceBannerExamples examples={aside.items} />
            ) : (
              <ShareResourceBannerGuides guides={aside.items} />
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default ShareResourceBanner;
